import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Box, Typography, Stack } from "@mui/material";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import ArrowIcon from "../../images/arrow.svg";
import { useTranslation } from "react-i18next";
import destinationSVG from "../../images/destination.svg";
import sourcevehicleSVG from "../../images/sourceVehicle.svg";
import {
  calculateDistanceBetweenPoints,
  getVehicleStatusColor,
  calculateDistanceBetweenStopAndVehicle,
  formatTime,
  getStatusPriority,
} from "./StopscrennFunctions";
import {
  fetchVehicleVisitStatus,
  updateVehicleVisitStatus,
  batchFetchVehicleVisitStatuses,
} from "./stopScreenAPI.js";
import {
  getvalueFromLocalstorage,
  useGetvalueFromLocalstorage,
} from "../../hooks/useGetvalueFromLocalstorage.js";
import { useSettings } from "../../context/SettingsContext";

const VehicleInfoPanel = ({
  vehicles = [],
  calculating = false,
  isLeftPanel = true,
  InfoSide,
  hideHeader = false,
  headerText = "",
  vehicleStopStatuses = {},
  routeCoordinates,
  vehicleDirections = {},
  stopsData = [],
  stopDistances = {},
  stopHighlighted = null,
  stopToStopDistances,
  highlightedStopId = null,
  panelKey = "panel",
  onArrivalCandidate,
  onLeadVehicle,
}) => {
  const { t } = useTranslation();
  const [vehicleSpeedHistory, setVehicleSpeedHistory] = useState({});
  const [historicalAvgSpeeds, setHistoricalAvgSpeeds] = useState({});
  const [onlyTowardsDestination, setOnlyTowardsDestination] = useState(false);

  const previousETAsRef = useRef({});
  const leadSignatureRef = useRef("");
  const [vehicleDistances, setVehicleDistances] = useState({});
  const lastApiCallTimestamps = useRef({});
  const cachedVisitStatuses = useRef({});
  const vehicleStopCrossings = useRef({});
  const updateInProgress = useRef(false);
  const initialDataLoaded = useRef(false);

  const { settings } = useSettings();
  const vehiclesStatusFilter = settings?.vehiclesStatus || [
    "active",
    "inactive",
  ];

  const CONSTANTS = {
    EARTH_RADIUS: 6371e3,
    AVG_SPEED: 25,
    TRAFFIC_MULTIPLIER: 1.1,
    MIN_STOP_TIME: 10,
    MAX_STOP_TIME: 6000,
    HISTORY_POINTS: 15,
    MIN_TIME_DIFF: 10,
    MAX_SPEED_THRESHOLD: 100,
    MIN_DISTANCE_THRESHOLD: 10,
    SPEED_UPDATE_INTERVAL: 300000,
    EMA_WEIGHT: 0.8,
    REACHED_DISTANCE_THRESHOLD: 0.1,
    STOP_PROXIMITY_THRESHOLD: 0.2,
    API_UPDATE_INTERVAL: 180000,
    CROSSING_THRESHOLD: 0.1,
  };

  console.log(stopToStopDistances, "stopToStopDistances");
  console.log(routeCoordinates, "routeCoordinates");
  console.log(stopDistances, "stopDistances");

  const shouldCallApi = useCallback(
    (vehicleId, stopId) => {
      const key = `${vehicleId}_${stopId}`;
      const lastCall = lastApiCallTimestamps.current[key] || 0;
      const now = Date.now();
      return now - lastCall >= CONSTANTS.API_UPDATE_INTERVAL;
    },
    [CONSTANTS.API_UPDATE_INTERVAL],
  );

  useEffect(() => {
    const loadSetting = async () => {
      const settingValue = await getvalueFromLocalstorage(
        "onlyTowardsDestination",
        false,
      );
      setOnlyTowardsDestination(settingValue);
    };
    loadSetting();
  }, []);

  const updateApiTimestamp = useCallback((vehicleId, stopId) => {
    const key = `${vehicleId}_${stopId}`;
    lastApiCallTimestamps.current[key] = Date.now();
  }, []);

  const calculateRealisticETA = useCallback(
    (distanceKm, vehicleId) => {
      if (!distanceKm || distanceKm <= 0) return { smoothedETA: null };

      const history = vehicleSpeedHistory[vehicleId];
      let effectiveSpeed;

      let realTimeSpeed = null;
      if (history && history.length >= 2) {
        const lastPoint = history[history.length - 1];
        const secondLastPoint = history[history.length - 2];
        const timeDiff =
          (lastPoint.timestamp - secondLastPoint.timestamp) / 1000;

        if (timeDiff >= CONSTANTS.MIN_TIME_DIFF) {
          const distance = calculateDistanceBetweenPoints(
            secondLastPoint,
            lastPoint,
          );
          if (
            distance !== null &&
            distance >= CONSTANTS.MIN_DISTANCE_THRESHOLD
          ) {
            const speed = distance / 1000 / (timeDiff / 3600);
            if (
              speed <= CONSTANTS.MAX_SPEED_THRESHOLD &&
              !isNaN(speed) &&
              isFinite(speed)
            ) {
              realTimeSpeed = speed;
            }
          }
        }
      }

      if (!realTimeSpeed && history && history.length > 0) {
        const reportedSpeed =
          parseFloat(history[history.length - 1]?.reportedSpeed) || 0;
        if (
          reportedSpeed > 5 &&
          reportedSpeed <= CONSTANTS.MAX_SPEED_THRESHOLD
        ) {
          realTimeSpeed = reportedSpeed;
        }
      }

      const historicalSpeed =
        historicalAvgSpeeds[vehicleId]?.avgSpeed || CONSTANTS.AVG_SPEED;
      effectiveSpeed = realTimeSpeed
        ? 0.7 * realTimeSpeed + 0.3 * historicalSpeed
        : historicalSpeed;

      let baseTime = (distanceKm / effectiveSpeed) * 3600;
      baseTime *= CONSTANTS.TRAFFIC_MULTIPLIER;

      const numTrafficLights = Math.floor((distanceKm * 1000) / 800);
      const avgTrafficLightDelay = 20;
      baseTime += numTrafficLights * avgTrafficLightDelay;

      const currentETA = Math.min(
        Math.max(Math.round(baseTime), CONSTANTS.MIN_STOP_TIME),
        CONSTANTS.MAX_STOP_TIME,
      );

      const previousETA = previousETAsRef.current[vehicleId] || currentETA;
      const smoothedETA =
        CONSTANTS.EMA_WEIGHT * currentETA +
        (1 - CONSTANTS.EMA_WEIGHT) * previousETA;

      previousETAsRef.current[vehicleId] = currentETA;

      return { smoothedETA: Math.round(smoothedETA), currentETA };
    },
    [vehicleSpeedHistory, historicalAvgSpeeds, CONSTANTS],
  );

  const calculateRoundRouteDistance = useCallback(
    (vehicleId, stopId, vehicleDirection, currentStraightDistance) => {
      const stopData = stopDistances[stopId];
      if (!stopData) {
        console.log(
          `Vehicle ${vehicleId} - No stop data for ${stopId}, using straight line`,
        );
        return currentStraightDistance;
      }

      const routeDistanceToSource =
        parseFloat(stopData.routeDistanceToSource) || 0;
      const routeDistanceToDestination =
        parseFloat(stopData.routeDistanceToDestination) || 0;

      let roundRouteDistance;

      if (vehicleDirection === "source_to_dest") {
        roundRouteDistance =
          routeDistanceToDestination + routeDistanceToDestination;
        console.log(
          `Vehicle ${vehicleId} - Round route (source_to_dest): ${routeDistanceToDestination} + ${routeDistanceToDestination} = ${roundRouteDistance}km`,
        );
      } else if (vehicleDirection === "dest_to_source") {
        roundRouteDistance = routeDistanceToSource + routeDistanceToSource;
        console.log(
          `Vehicle ${vehicleId} - Round route (dest_to_source): ${routeDistanceToSource} + ${routeDistanceToSource} = ${roundRouteDistance}km`,
        );
      } else {
        console.log(
          `Vehicle ${vehicleId} - Unknown direction ${vehicleDirection}, using straight line`,
        );
        return currentStraightDistance;
      }

      const progressFactor = Math.max(
        0,
        Math.min(
          1,
          currentStraightDistance /
            Math.max(routeDistanceToSource, routeDistanceToDestination),
        ),
      );
      roundRouteDistance = Math.max(
        0,
        roundRouteDistance * (1 - progressFactor * 0.1),
      );

      console.log(
        `Vehicle ${vehicleId} - Final round route distance: ${roundRouteDistance}km`,
      );
      return roundRouteDistance;
    },
    [stopDistances],
  );

  // ← FIXED: Changed function name to batchFetchVehicleVisitStatuses
  const batchLoadVisitStatuses = useCallback(async () => {
    if (!stopsData.length || !vehicles.length) return;

    console.log("Batch loading visit statuses from API...");

    const stopIds = [
      ...new Set(
        stopsData
          .map(
            (stop) =>
              stop.stopId ||
              Object.keys(stopDistances).find(
                (id) => stopDistances[id].stopName === stop.name,
              ),
          )
          .filter(Boolean),
      ),
    ];

    for (const stopId of stopIds) {
      try {
        const vehicleIds = vehicles.map((v) => v.device_id);

        // ← FIXED: Using the correct imported function
        const batchStatuses = await batchFetchVehicleVisitStatuses(
          stopId,
          vehicleIds,
        );

        Object.entries(batchStatuses).forEach(([vehicleId, visitStatus]) => {
          if (visitStatus) {
            const cacheKey = `${vehicleId}_${stopId}`;
            cachedVisitStatuses.current[cacheKey] = visitStatus;

            if (visitStatus.visitedDirection === vehicleDirections[vehicleId]) {
              if (!vehicleStopCrossings.current[vehicleId]) {
                vehicleStopCrossings.current[vehicleId] = {};
              }
              vehicleStopCrossings.current[vehicleId][stopId] = true;
              console.log(
                `Cached: Vehicle ${vehicleId} crossed stop ${stopId}`,
              );
            }
          }
        });

        await new Promise((resolve) => setTimeout(resolve, 100));
      } catch (error) {
        console.error(`Failed to batch load for stop ${stopId}:`, error);
      }
    }

    console.log("Finished batch loading visit statuses");
  }, [stopsData, vehicles, stopDistances, vehicleDirections]);

  // Reset crossing status - defined BEFORE calculateEnhancedDistance
  const resetVehicleCrossing = useCallback(
    (vehicleId, stopId) => {
      if (vehicleStopCrossings.current[vehicleId]) {
        vehicleStopCrossings.current[vehicleId][stopId] = false;
        delete cachedVisitStatuses.current[`${vehicleId}_${stopId}`];

        if (shouldCallApi(vehicleId, stopId)) {
          updateVehicleVisitStatus(
            stopId,
            vehicleId,
            vehicleDirections[vehicleId],
            parseFloat(stopDistances[stopId]?.routeDistanceToSource) ?? null,
            parseFloat(stopDistances[stopId]?.routeDistanceToDestination) ??
              null,
            true,
            0,
            null,
            null,
            stopToStopDistances,
            routeCoordinates,
          )
            .then(() => {
              updateApiTimestamp(vehicleId, stopId);
              console.log(
                `Background API reset: Vehicle ${vehicleId} reached stop ${stopId}`,
              );
            })
            .catch((err) => {
              console.error(`Background API reset failed:`, err);
            });
        }
      }
    },
    [
      vehicleDirections,
      stopDistances,
      stopToStopDistances,
      routeCoordinates,
      shouldCallApi,
      updateApiTimestamp,
    ],
  );

  // ← FIXED: Added missing dependencies
  const calculateEnhancedDistance = useCallback(
    (
      vehiclePos,
      targetStop,
      vehicleDirection,
      vehicleId,
      highlightedStopName,
      stopId,
    ) => {
      const distToHighlighted =
        vehiclePos && targetStop
          ? calculateDistanceBetweenStopAndVehicle(vehiclePos, targetStop)
          : null;

      const cacheKey = `${vehicleId}_${stopId}`;
      const visitStatus = cachedVisitStatuses.current[cacheKey];

      if (
        distToHighlighted !== null &&
        distToHighlighted <= CONSTANTS.REACHED_DISTANCE_THRESHOLD
      ) {
        console.log(
          `Vehicle ${vehicleId} - At highlighted stop ${highlightedStopName}`,
        );
        if (stopId) {
          resetVehicleCrossing(vehicleId, stopId); // Now defined before
        }
        return { distance: 0, eta: null, isRoundRoute: false };
      }

      if (!highlightedStopName || !vehicleDirection || !stopId) {
        console.log(
          `Vehicle ${vehicleId} - Missing stop info, using Haversine`,
        );
        const etaData = distToHighlighted
          ? calculateRealisticETA(distToHighlighted, vehicleId)
          : { smoothedETA: null };
        return {
          distance: distToHighlighted ?? null,
          eta: etaData.smoothedETA,
          isRoundRoute: false,
        };
      }

      const previousDistance =
        vehicleDistances[vehicleId]?.calculatedDistance ?? null;
      const hasCrossedFromAPI =
        visitStatus?.visitedDirection === vehicleDirection;

      let hasCrossedRealTime = false;
      if (!vehicleStopCrossings.current[vehicleId]) {
        vehicleStopCrossings.current[vehicleId] = {};
      }
      const wasAlreadyCrossed =
        vehicleStopCrossings.current[vehicleId][stopId] || false;
      const justCrossed =
        !wasAlreadyCrossed &&
        previousDistance !== null &&
        previousDistance <= CONSTANTS.CROSSING_THRESHOLD &&
        distToHighlighted > CONSTANTS.CROSSING_THRESHOLD;

      if (justCrossed) {
        vehicleStopCrossings.current[vehicleId][stopId] = true;
        hasCrossedRealTime = true;

        if (shouldCallApi(vehicleId, stopId)) {
          updateVehicleVisitStatus(
            stopId,
            vehicleId,
            vehicleDirection,
            parseFloat(stopDistances[stopId]?.routeDistanceToSource) ?? null,
            parseFloat(stopDistances[stopId]?.routeDistanceToDestination) ??
              null,
            false,
            distToHighlighted,
            null,
            vehicleDirection,
            stopToStopDistances,
            routeCoordinates,
          )
            .then(() => {
              updateApiTimestamp(vehicleId, stopId);
              console.log(
                `Background API update: Vehicle ${vehicleId} crossed stop ${stopId}`,
              );
            })
            .catch((err) => {
              console.error(`Background API update failed:`, err);
            });
        }
      }

      const hasCrossed = hasCrossedFromAPI || hasCrossedRealTime;

      let calculatedDistance;
      let isRoundRoute = false;

      if (hasCrossed) {
        calculatedDistance = calculateRoundRouteDistance(
          vehicleId,
          stopId,
          vehicleDirection,
          distToHighlighted,
        );
        isRoundRoute = true;
        console.log(
          `Vehicle ${vehicleId} - Round route: ${calculatedDistance}km`,
        );
      } else {
        calculatedDistance = distToHighlighted;
        isRoundRoute = false;
      }

      const etaData = calculatedDistance
        ? calculateRealisticETA(calculatedDistance, vehicleId)
        : { smoothedETA: null };
      const calculatedETA =
        etaData.smoothedETA === null || etaData.smoothedETA === Infinity
          ? null
          : etaData.smoothedETA;

      return {
        distance: calculatedDistance ?? null,
        eta: calculatedETA,
        isRoundRoute,
      };
    },
    [
      stopDistances,
      calculateRealisticETA,
      calculateRoundRouteDistance,
      vehicleDistances,
      CONSTANTS,
      shouldCallApi,
      updateApiTimestamp,
      stopToStopDistances,
      routeCoordinates,
      vehicleDirections, // ← ADDED
      resetVehicleCrossing, // ← ADDED
    ],
  );

  useEffect(() => {
    const initializeData = async () => {
      if (
        vehicles.length > 0 &&
        stopsData.length > 0 &&
        !initialDataLoaded.current
      ) {
        await batchLoadVisitStatuses();
        initialDataLoaded.current = true;
      }
    };

    initializeData();
  }, [vehicles, stopsData, batchLoadVisitStatuses]);

  useEffect(() => {
    const updateAllVehicleDistances = () => {
      if (updateInProgress.current || !initialDataLoaded.current) return;
      updateInProgress.current = true;

      try {
        const updatedDistances = {};
        for (const vehicle of vehicles) {
          const vehicleId = vehicle.device_id;
          const targetStop =
            stopHighlighted ||
            vehicle.stopPosition ||
            (stopsData.length > 0 ? stopsData[0] : null);
          const highlightedStopName =
            stopHighlighted?.name ||
            stopsData.find(
              (stop) =>
                stop.latitude === vehicle.stopPosition?.latitude &&
                stop.longitude === vehicle.stopPosition?.longitude,
            )?.name ||
            "Unknown";
          const stopId =
            stopHighlighted?.stopId ||
            Object.keys(stopDistances).find(
              (id) => stopDistances[id].stopName === highlightedStopName,
            ) ||
            null;

          const result = calculateEnhancedDistance(
            vehicle.position,
            targetStop,
            vehicleDirections[vehicleId],
            vehicleId,
            highlightedStopName,
            stopId,
          );
          updatedDistances[vehicleId] = {
            calculatedDistance: result.distance,
            calculatedETA: result.eta,
            isRoundRoute: result.isRoundRoute,
          };
        }
        setVehicleDistances(updatedDistances);
      } catch (error) {
        console.error("Error updating vehicle distances:", error);
      } finally {
        updateInProgress.current = false;
      }
    };

    const timeoutId = setTimeout(updateAllVehicleDistances, 500);
    return () => clearTimeout(timeoutId);
  }, [
    vehicles,
    vehicleDirections,
    stopHighlighted,
    stopsData,
    stopDistances,
    calculateEnhancedDistance,
    initialDataLoaded,
  ]);

  const updateVehicleSpeedHistory = useCallback(
    (vehicleId, position, speed) => {
      const now = Date.now();
      const newPoint = {
        lat: position.lat,
        lng: position.lng,
        timestamp: now,
        reportedSpeed: speed || 0,
      };

      setVehicleSpeedHistory((prev) => {
        const currentHistory = prev[vehicleId] || [];
        const updatedHistory = [...currentHistory, newPoint];
        const trimmedHistory = updatedHistory.slice(-CONSTANTS.HISTORY_POINTS);
        return { ...prev, [vehicleId]: trimmedHistory };
      });
    },
    [CONSTANTS.HISTORY_POINTS],
  );

  useEffect(() => {
    vehicles.forEach((vehicle) => {
      if (vehicle.position && vehicle.device_id) {
        updateVehicleSpeedHistory(
          vehicle.device_id,
          vehicle.position,
          vehicle.speed,
        );
      }
    });
  }, [vehicles, updateVehicleSpeedHistory]);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setHistoricalAvgSpeeds((prev) => {
        const updatedSpeeds = { ...prev };
        Object.keys(vehicleSpeedHistory).forEach((vehicleId) => {
          const history = vehicleSpeedHistory[vehicleId];
          if (!history || history.length < 2) return;

          let totalDistance = 0;
          let totalTime = 0;
          let validSegments = 0;

          for (let i = 1; i < history.length; i++) {
            const prevPoint = history[i - 1];
            const currPoint = history[i];
            const timeDiff = (currPoint.timestamp - prevPoint.timestamp) / 1000;
            if (timeDiff < CONSTANTS.MIN_TIME_DIFF) continue;

            const distance = calculateDistanceBetweenPoints(
              prevPoint,
              currPoint,
            );
            if (
              distance !== null &&
              distance >= CONSTANTS.MIN_DISTANCE_THRESHOLD
            ) {
              const segmentSpeed = distance / 1000 / (timeDiff / 3600);
              if (
                segmentSpeed <= CONSTANTS.MAX_SPEED_THRESHOLD &&
                !isNaN(segmentSpeed) &&
                isFinite(segmentSpeed)
              ) {
                totalDistance += distance / 1000;
                totalTime += timeDiff / 3600;
                validSegments++;
              }
            }
          }

          if (validSegments >= Math.floor(CONSTANTS.HISTORY_POINTS / 2)) {
            const avgSpeed = totalDistance / totalTime;
            if (
              !isNaN(avgSpeed) &&
              isFinite(avgSpeed) &&
              avgSpeed >= 1 &&
              avgSpeed <= CONSTANTS.MAX_SPEED_THRESHOLD
            ) {
              updatedSpeeds[vehicleId] = {
                avgSpeed,
                lastUpdated: now,
              };
            }
          }
        });
        return updatedSpeeds;
      });
    }, CONSTANTS.SPEED_UPDATE_INTERVAL);

    return () => clearInterval(interval);
  }, [vehicleSpeedHistory, CONSTANTS]);

  // Direction preference based on panel side
  const preferredDirection = isLeftPanel ? "dest_to_source" : "source_to_dest";

  // This state now holds your setting loaded via useEffect
  // const [onlyTowardsDestination, setOnlyTowardsDestination] = useState(false);

  // const filteredVehicles = onlyTowardsDestination
  //   ? vehicles.filter((v) => v.direction === preferredDirection) // filter by direction
  //   : vehicles; // show all vehicles if config says false

  const statusFilteredVehicles = vehicles.filter((v) => {
    const vehiclesStatusFilter = settings?.vehiclesStatus || [
      "active",
      "inactive",
    ];
    const status = (v.status || "").toUpperCase();
    if (status === "ACTIVE") return vehiclesStatusFilter.includes("active");
    return vehiclesStatusFilter.includes("inactive");
  });

  const filteredVehicles = onlyTowardsDestination
    ? statusFilteredVehicles.filter((v) => v.direction === preferredDirection)
    : statusFilteredVehicles;

  const displayedVehicles = [...filteredVehicles].sort((a, b) => {
    const statusA = getVehicleStatusColor(a).status || a.status;
    const statusB = getVehicleStatusColor(b).status || b.status;
    const priorityA = getStatusPriority(statusA);
    const priorityB = getStatusPriority(statusB);
    if (priorityA !== priorityB) return priorityA - priorityB;

    const distanceA =
      vehicleDistances[a.device_id]?.calculatedDistance ??
      (a.position && stopHighlighted
        ? calculateDistanceBetweenStopAndVehicle(a.position, stopHighlighted)
        : (a.distanceToStop ?? Infinity));
    const distanceB =
      vehicleDistances[b.device_id]?.calculatedDistance ??
      (b.position && stopHighlighted
        ? calculateDistanceBetweenStopAndVehicle(b.position, stopHighlighted)
        : (b.distanceToStop ?? Infinity));

    return distanceA - distanceB;
  });

  const arrivalCandidate = useMemo(() => {
    let earliest = null;

    displayedVehicles.forEach((vehicle) => {
      const status = String(vehicle.status || "").toUpperCase();
      if (status === "DEAD" || status === "INACTIVE") return;

      const metrics = vehicleDistances[vehicle.device_id];
      const etaSeconds = metrics?.calculatedETA;
      const distance = metrics?.calculatedDistance;
      const destination = vehicle.destinationLabel;
      if (!vehicle.name || !destination || destination === "---") return;
      if (!Number.isFinite(etaSeconds) || etaSeconds < 0) return;
      if (
        distance != null &&
        distance <= CONSTANTS.REACHED_DISTANCE_THRESHOLD
      ) {
        return;
      }
      if (earliest && etaSeconds >= earliest.etaSeconds) return;

      earliest = {
        vehicleId: vehicle.device_id,
        vehicleName: vehicle.name,
        destination,
        etaSeconds,
        stopId: highlightedStopId,
        tripStatus: vehicle.tripStatus || null,
      };
    });

    return earliest;
  }, [displayedVehicles, vehicleDistances, highlightedStopId, CONSTANTS.REACHED_DISTANCE_THRESHOLD]);

  useEffect(() => {
    onArrivalCandidate?.(panelKey, arrivalCandidate);
  }, [arrivalCandidate, onArrivalCandidate, panelKey]);

  useEffect(() => {
    if (!onLeadVehicle) return;
    const vehicle = displayedVehicles[0];
    if (!vehicle) {
      if (leadSignatureRef.current === "") return;
      leadSignatureRef.current = "";
      onLeadVehicle(null);
      return;
    }

    const metrics = vehicleDistances[vehicle.device_id] || {};
    const distance = metrics.calculatedDistance;
    const etaSeconds = metrics.calculatedETA;
    const isReached = distance != null && distance <= CONSTANTS.REACHED_DISTANCE_THRESHOLD;
    let stopName = vehicleStopStatuses[vehicle.device_id]?.stopName || "";
    if (!stopName && vehicle.position && stopsData.length > 0) {
      let minDistance = Infinity;
      let closestStop = null;
      stopsData.forEach((stop) => {
        const dist = calculateDistanceBetweenStopAndVehicle(vehicle.position, stop);
        if (dist !== null && dist < minDistance) {
          minDistance = dist;
          closestStop = stop;
        }
      });
      if (closestStop?.name) stopName = closestStop.name;
    }

    const payload = {
      name: vehicle.name || "",
      atStop: stopName,
      towards: vehicle.destinationLabel || "",
      distanceText: isReached
        ? "Reached"
        : distance != null
          ? `${Number(distance).toFixed(1)} km`
          : "",
      etaText: isReached
        ? "Reached"
        : formatTime(etaSeconds)?.replace("m", " min")?.replace("s", " s") || "",
    };
    const signature = JSON.stringify(payload);
    if (leadSignatureRef.current === signature) return;
    leadSignatureRef.current = signature;
    onLeadVehicle(payload);
  }, [
    displayedVehicles,
    vehicleDistances,
    vehicleStopStatuses,
    stopsData,
    onLeadVehicle,
    CONSTANTS.REACHED_DISTANCE_THRESHOLD,
  ]);

  useEffect(() => {
    return () => onArrivalCandidate?.(panelKey, null);
  }, [onArrivalCandidate, panelKey]);

  return (
    <Box
      sx={{
        borderRadius: "20px",
        overflowY: "auto",
        boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
        bgcolor: "#E7EAEF",
        height: "90%",
        display: "flex",
        flexDirection: "column",
        mb: !InfoSide ? 2 : 0,
      }}
    >
      {!hideHeader && (
        <Box
          sx={{
            p: 2,
            bgcolor: "#183059",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "white",
            fontWeight: "bold",
            fontSize: 24,
            gap: 1,
          }}
        >
          {headerText ? (
            <Typography variant="h6">{headerText}</Typography>
          ) : isLeftPanel ? (
            <>
              {t("Next")} {t("Vehicles")}
              <img src={ArrowIcon} alt="arrow" />
              <DirectionsCarIcon style={{ width: "32px", height: "32px" }} />
            </>
          ) : (
            <>
              <DirectionsCarIcon style={{ width: "32px", height: "34px" }} />
              <img
                src={ArrowIcon}
                style={{ transform: "scaleX(-1)" }}
                alt="arrow"
              />
              {t("Next")} {t("Vehicles")}
            </>
          )}
        </Box>
      )}

      <Box sx={{ p: 2, flexGrow: 1 }}>
        {displayedVehicles.length === 0 ? (
          <Typography sx={{ textAlign: "center", mt: 4 }}>
            {calculating ? t("Calculating...") : t("No vehicles available")}
          </Typography>
        ) : (
          displayedVehicles.map((v) => {
            const targetStop =
              stopHighlighted ||
              v.stopPosition ||
              (stopsData.length > 0 ? stopsData[0] : null);
            const highlightedStopName =
              stopHighlighted?.name ||
              stopsData.find(
                (stop) =>
                  stop.latitude === v.stopPosition?.latitude &&
                  stop.longitude === v.stopPosition?.longitude,
              )?.name ||
              "Unknown";

            const calculatedDistance =
              vehicleDistances[v.device_id]?.calculatedDistance ?? null;
            const calculatedETA =
              vehicleDistances[v.device_id]?.calculatedETA ?? null;
            const isRoundRoute =
              vehicleDistances[v.device_id]?.isRoundRoute ?? false;
            const isReached =
              calculatedDistance != null &&
              calculatedDistance <= CONSTANTS.REACHED_DISTANCE_THRESHOLD;

            const stopStatus = vehicleStopStatuses[v.device_id];

            let stopName = stopStatus?.stopName || "Unknown";
            if (!stopStatus?.stopName && v.position && stopsData.length > 0) {
              let minDistance = Infinity;
              let closestStop = null;
              stopsData.forEach((stop) => {
                const dist = calculateDistanceBetweenStopAndVehicle(
                  v.position,
                  stop,
                );
                if (dist !== null && dist < minDistance) {
                  minDistance = dist;
                  closestStop = stop;
                }
              });
              if (closestStop) stopName = closestStop.name || "Unknown";
            }

            const vehicleStatusStyle = getVehicleStatusColor(v);
            const showETA =
              vehicleStatusStyle.statusText === null && !isReached;

            return (
              <Box
                key={v.device_id}
                sx={{
                  mb: 2,
                  p: 1,
                  borderRadius: "12px",
                  bgcolor: "#FFFFFF",
                  color: "white",
                  boxShadow: "-2px 2px 4px 0px #00000033",
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ width: "100%" }}
                >
                  <Stack
                    direction="column"
                    alignItems="flex-start"
                    spacing={0.5}
                    sx={{ minWidth: 0, flex: 1 }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ minWidth: 0 }}
                    >
                      <img
                        src={sourcevehicleSVG}
                        alt=""
                        style={{
                          width: "32px",
                          height: "32px",
                        }}
                      />
                      <Typography
                        sx={{
                          color:
                            v.status === "DEAD" || v.status === "INACTIVE"
                              ? "#183059"
                              : "#183059",
                          fontWeight: 600,
                          fontSize: 16,
                          wordBreak: "break-word",
                        }}
                      >
                        {v.name}
                      </Typography>
                    </Stack>
                    <Typography
                      sx={{
                        color: "#414958",
                        fontSize: 12,
                        fontWeight: 500,
                        ml: 5,
                      }}
                    >
                      At {stopName}
                    </Typography>
                    {v.tripStatus?.message ? (
                      <Typography
                        sx={{
                          color:
                            v.tripStatus.eventType === "LATE_TO_STOP" ||
                            v.tripStatus.eventType === "VEHICLE_DELAYED"
                              ? "#B45309"
                              : "#183059",
                          fontSize: 12,
                          fontWeight: 600,
                          ml: 5,
                        }}
                      >
                        {t(v.tripStatus.message)}
                      </Typography>
                    ) : null}
                  </Stack>
                  <Typography
                    sx={{
                      color: "#6B7280",
                      fontSize: 14,
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                      textAlign: "center",
                      flex: 1,
                    }}
                  >
                    {isReached
                      ? t("Reached")
                      : calculatedDistance != null
                        ? `${calculatedDistance.toFixed(1)} km`
                        : "---"}
                  </Typography>

                  <Box
                    sx={{
                      bgcolor: isReached
                        ? "#008C23"
                        : isRoundRoute
                          ? "#008C23"
                          : vehicleStatusStyle.bgColor,
                      color: vehicleStatusStyle.textColor,
                      px: 2,
                      py: 0.25,
                      borderRadius: "20px",
                      fontWeight: 600,
                      fontSize: 14,
                      minWidth: 90,
                      textAlign: "center",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {isReached
                      ? t("Reached")
                      : formatTime(calculatedETA)
                          ?.replace("m", " min")
                          ?.replace("s", " s") || "---"}
                  </Box>
                </Stack>

                {!InfoSide ? null : v.status !== "DEAD" &&
                  v.status !== "INACTIVE" ? (
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ ml: 4 }}
                  >
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: "50%",
                        // background: "#973b0dff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <img src={destinationSVG} alt="" />
                    </Box>
                    <Typography sx={{ color: "#414958", fontSize: 15 }}>
                      Towards{" "}
                      <span
                        style={{
                          fontWeight: 600,
                          color: "#111214",
                          fontSize: "18px",
                        }}
                      >
                        {v.destinationLabel || "---"}
                      </span>
                    </Typography>
                  </Stack>
                ) : null}
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
};

export default VehicleInfoPanel;
